import { useNavigate } from 'react-router-dom';
import { useFormik } from 'formik';
import Card from '../../components/card/Card';
import H from '../../components/texts/h/H';
import Icon from '../../components/icon/Icon';
import { routes } from '../../routes/routePaths';
import P from '../../components/texts/p/P';
import Input from '../../components/input/Input';
import DropDown from '../../components/dropdown/DropDown';
import Span from '../../components/texts/span/Span';
import Button from '../../components/button/Button';
import { getRoles, saveUser } from '../../services/UserService';
import { useEffect, useState } from 'react';
import { Toast, useToast } from '../../components/toast/toast';
import Spinner from '../../components/Spinner/spinner';

const NewUser = () => {
  const [roles, setRoles] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const toastResponseMap = new Map([
    [
      201,
      {
        action: () => {
          useToast.success({
            title: 'Guardado con éxito',
            id: 'user-save-toast',
            description: '',
            closeButton: true,
            autoClose: 5000,
          });
          setIsLoading(false);
          navigate(routes.users);
        },
      },
    ],
    [
      409,
      {
        action: () => {
          setIsLoading(false);

          useToast.error({
            title: 'Ya existe un usuario para el email ingresado',
            id: 'user-save-toast',
            description: '',
            closeButton: true,
            autoClose: 5000,
          });
        },
      },
    ],
  ]);

  useEffect(() => {
    getRoles().then((response) => {
      if (response.success) {
        setRoles(response.data);
      }
    });
  }, []);
  const navigate = useNavigate();

  const validate = (values: any) => {
    const errors: any = {};

    if (!values.name) {
      errors.name = 'El nombre es obligatorio';
    }

    if (!values.lastname) {
      errors.lastname = 'El apellido es obligatorio';
    }

    if (!values.email) {
      errors.email = 'El email es obligatorio';
    } else if (
      !/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,4}$/i.test(values.email)
    ) {
      errors.email = 'Formato de email inválido';
    }

    if (!values.cuit) {
      errors.cuit = 'El CUIT es obligatorio';
    } else if (!/^\d+$/.test(values.cuit)) {
      errors.cuit = 'Solo números';
    } else if (values.cuit.length !== 11) {
      errors.cuit = 'Debe tener 11 dígitos';
    }

    if (!values.role) {
      errors.role = 'El rol es obligatorio';
    }

    return errors;
  };

  const formik = useFormik({
    initialValues: {
      name: '',
      lastname: '',
      email: '',
      cuit: '',
      role: '',
    },
    validate,
    validateOnChange: true,
    onSubmit: (values) => {
      const payload = {
        email: values.email,
        firstName: values.name,
        lastName: values.lastname,
        roleCode: values.role,
        ci: values.cuit,
      };
      setIsLoading(true);
      saveUser(payload).then((response) => {
        const toastHandler = toastResponseMap.get(response.status);

        if (toastHandler) {
          setIsLoading(false);
          toastHandler.action();
        } else {
          setIsLoading(false);
          useToast.error({
            title: 'Error al guardar el usuario',
            id: 'user-save-toast',
            description: response.message || '',
            closeButton: true,
            autoClose: 5000,
          });
        }
      });
    },
  });

  return (
    <Card
      variant='neutral'
      modifier='inverted'
      spacing='4'
      height='-webkit-fill-available'
      width='54vw'
    >
      <Toast id='user-save-toast' />
      <Card display='flex' justifyContent='space-between' alignItems='center'>
        <Card display='flex' gap='16px' alignItems='center'>
          <Icon
            name='chevron_left_outline'
            height={24}
            width={24}
            cursor='pointer'
            onClick={() => navigate(routes.users)}
          />
          <H type='1' weight='bold' modifier='strong'>
            Alta de nuevo usuario
          </H>
        </Card>
      </Card>
      <H
        type='5'
        variant='neutral'
        weight='bold'
        modifier='strong'
        withMargin={false}
      >
        Datos del usuario
      </H>
      <P type='1' variant='neutral'>
        Enviaremos un email a la dirección ingresada. El link expira en 1 hora,
        si eso sucede, tendrás que volver a crear el usuario.
      </P>

      <form onSubmit={formik.handleSubmit}>
        <Card
          display='flex'
          gap='16px'
          justifyItems='center'
          justifyContent='center'
        >
          <Card width='100%'>
            <Input
              id='name'
              label='Nombre'
              placeholder='Nombre'
              size='md'
              type='text'
              value={formik.values.name}
              onChange={formik.handleChange}
              inputStatus={formik.errors.name ? 'error' : 'normal'}
            />
            <Span variant='danger' type='2'>
              {formik.errors.name}
            </Span>
          </Card>
          <Card width='100%'>
            <Input
              id='lastname'
              label='Apellido'
              placeholder='Apellido'
              size='md'
              type='text'
              value={formik.values.lastname}
              onChange={formik.handleChange}
              inputStatus={formik.errors.lastname ? 'error' : 'normal'}
            />
            <Span variant='danger' type='2'>
              {formik.errors.lastname}
            </Span>
          </Card>
          <Card width='100%'>
            <DropDown
              status={formik.errors.role ? 'error' : 'normal'}
              size='md'
              id='role'
              items={roles.map((r) => ({ id: r.roleCode, label: r.label }))}
              title={
                roles.find((r) => r.roleCode === formik.values.role)?.label ||
                'Seleccioná'
              }
              label='Rol'
              onChange={(e) => {
                const selected = roles.find((r) => r.label === e);
                formik.setFieldValue('role', selected?.roleCode || '');
              }}
            />
            <Span variant='danger' type='2'>
              {formik.errors.role}
            </Span>
          </Card>
        </Card>

        <Card display='flex' gap='16px' margin={{ top: '4' }}>
          <Card width='100%'>
            <Input
              id='email'
              label='Email'
              placeholder='email@dominio.com'
              size='md'
              type='text'
              value={formik.values.email}
              onChange={formik.handleChange}
              inputStatus={formik.errors.email ? 'error' : 'normal'}
            />
            <Span variant='danger' type='2'>
              {formik.errors.email}
            </Span>
          </Card>
          <Card width='100%'>
            <Input
              id='cuit'
              label='CUIT'
              placeholder='Sin guiones ni espacios'
              size='md'
              type='text'
              value={formik.values.cuit}
              onChange={formik.handleChange}
              inputStatus={formik.errors.cuit ? 'error' : 'normal'}
            />
            <Span variant='danger' type='2'>
              {formik.errors.cuit}
            </Span>
          </Card>
        </Card>

        <Card display='flex' justifyContent='flex-end' margin={{ top: '4' }}>
          <Button
            type='submit'
            size='lg'
            variant='primary'
            disabled={isLoading}
          >
            {isLoading ? (
              <Spinner size='lg' variant='primary' />
            ) : (
              'Generar usuario'
            )}
          </Button>
        </Card>
      </form>
    </Card>
  );
};

export default NewUser;
